import { useEffect, useState } from 'react';
import { LinkSimple } from '@phosphor-icons/react';
import { useSceneStore } from '../../stores/sceneStore';
import { api } from '../../lib/api-client';
import type { Layer, Asset } from '@spine-workspace/shared';

interface BoneAttachmentProps {
  layer: Layer;
  allLayers: Layer[];
}

const selectCls = `w-full bg-bg-input border border-border text-text-primary rounded-md
  px-3 py-[7px] text-sm font-sans
  hover:bg-bg-hover focus:outline-none focus:border-accent
  transition-all duration-fast appearance-none`;

export default function BoneAttachment({ layer, allLayers }: BoneAttachmentProps) {
  const setLayerAttachment = useSceneStore((s) => s.setLayerAttachment);
  const [parentBones, setParentBones] = useState<string[]>([]);
  const { parentLayerId, boneName } = layer.attachment;

  const otherLayers = allLayers.filter((l) => l.id !== layer.id);

  useEffect(() => {
    if (!parentLayerId) { setParentBones([]); return; }
    const parent = allLayers.find((l) => l.id === parentLayerId);
    if (!parent) return;
    api.get<{ asset: Asset }>(`/api/assets/${parent.assetId}`)
      .then(({ asset }) => setParentBones(asset.metadata?.bones ?? []))
      .catch(() => setParentBones([]));
  }, [parentLayerId, allLayers]);

  return (
    <div className="space-y-2">
      {/* Parent Layer */}
      <div>
        <label className="block font-sans text-xs font-medium text-text-secondary mb-1.5">
          <span className="flex items-center gap-1"><LinkSimple size={11} /> Parent Layer</span>
        </label>
        <div className="relative">
          <select
            className={selectCls}
            value={parentLayerId ?? ''}
            onChange={(e) => {
              const val = e.target.value || null;
              setLayerAttachment(layer.id, val, null);
            }}
          >
            <option value="">None</option>
            {otherLayers.map((l) => (
              <option key={l.id} value={l.id}>{l.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Bone */}
      {parentLayerId && (
        <div>
          <label className="block font-sans text-xs font-medium text-text-secondary mb-1.5">Bone</label>
          <div className="relative">
            <select
              className={selectCls}
              value={boneName ?? ''}
              onChange={(e) => setLayerAttachment(layer.id, parentLayerId, e.target.value || null)}
            >
              <option value="">Select bone…</option>
              {parentBones.map((b) => (
                <option key={b} value={b}>{b}</option>
              ))}
            </select>
          </div>
        </div>
      )}
    </div>
  );
}
